import React, { useEffect, useMemo, useRef } from 'react';
import { Thumbnail } from '@remotion/player';
import { AbsoluteFill, Img } from 'remotion';
import { POST_LAYOUTS } from '@/lib/layouts/registry';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { cn } from '@/lib/utils';

const RENDERER_SETTLE_MS = 300;

const DESIGN = {
  portrait: { width: 1080, height: 1920 },
  landscape: { width: 1920, height: 1080 },
} as const;

const MutedFallback: React.FC<{ testId?: string }> = ({ testId }) => (
  <div className="h-full w-full bg-muted" data-testid={testId} />
);

const stillMedia = (
  stillUrl: string | null | undefined,
  fallbackTestId: string | undefined,
  remotion: boolean,
) => {
  if (!stillUrl) return <MutedFallback testId={fallbackTestId} />;
  if (remotion) {
    return (
      <Img
        src={stillUrl}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }
  return <img src={stillUrl} alt="" className="h-full w-full object-cover" />;
};

/** Still-frame composition: same registry Renderer as export, no playing video. */
const StillThumbComposition: React.FC<{
  layout: PostLayoutConfig;
  stillUrl?: string | null;
  fallbackTestId?: string;
}> = ({ layout, stillUrl, fallbackTestId }) => {
  const def = POST_LAYOUTS[layout.kind as keyof typeof POST_LAYOUTS];
  if (!def) {
    return <AbsoluteFill>{stillMedia(stillUrl, fallbackTestId, true)}</AbsoluteFill>;
  }
  const Renderer = def.Renderer;
  const media = stillMedia(stillUrl, fallbackTestId, true);
  return (
    <AbsoluteFill>
      <Renderer
        config={layout as never}
        media={media}
        renderClip={() => stillMedia(stillUrl, fallbackTestId, true)}
      />
    </AbsoluteFill>
  );
};

export function LayoutStillThumb({
  layout,
  stillUrl,
  className,
  testId,
  aspect = 'portrait',
  mountRenderer = true,
  onRendererSettled,
}: {
  layout: PostLayoutConfig | null;
  stillUrl?: string | null;
  className?: string;
  testId?: string;
  aspect?: 'portrait' | 'landscape';
  /** When false, skip Thumbnail and show the raw still / muted box. */
  mountRenderer?: boolean;
  onRendererSettled?: () => void;
}) {
  const fallbackTestId = testId ? `${testId}-fallback` : undefined;
  const design = DESIGN[aspect];
  const hasRenderer = !!layout && !!POST_LAYOUTS[layout.kind as keyof typeof POST_LAYOUTS];
  const inputProps = useMemo(
    () => ({ layout: layout as PostLayoutConfig, stillUrl: stillUrl ?? null, fallbackTestId }),
    [layout, stillUrl, fallbackTestId],
  );
  const settledRef = useRef(false);
  const onRendererSettledRef = useRef(onRendererSettled);
  onRendererSettledRef.current = onRendererSettled;

  const markSettled = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    onRendererSettledRef.current?.();
  };

  useEffect(() => {
    settledRef.current = false;
    if (!mountRenderer) return undefined;
    if (!hasRenderer) {
      markSettled();
      return undefined;
    }
    const timeoutId = window.setTimeout(markSettled, RENDERER_SETTLE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [mountRenderer, hasRenderer, layout, stillUrl, aspect]);

  const showRenderer = mountRenderer && hasRenderer;

  return (
    <div
      className={cn(
        'overflow-hidden bg-muted',
        aspect === 'portrait' ? 'aspect-[9/16]' : 'aspect-video',
        className,
      )}
      data-testid={testId}
    >
      {showRenderer ? (
        <Thumbnail
          component={StillThumbComposition as React.FC<Record<string, unknown>>}
          inputProps={inputProps}
          frameToDisplay={0}
          durationInFrames={30}
          compositionWidth={design.width}
          compositionHeight={design.height}
          fps={30}
          style={{ width: '100%', height: '100%' }}
          errorFallback={() => {
            queueMicrotask(markSettled);
            return stillUrl
              ? <img src={stillUrl} alt="" className="h-full w-full object-cover" />
              : <MutedFallback testId={fallbackTestId} />;
          }}
        />
      ) : stillMedia(stillUrl, fallbackTestId, false)}
    </div>
  );
}
